import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAutomationJobs, AutomationJob } from '@/hooks/useAutomationJobs';
import { Play, Pause, Square, Trash2, Download, Loader2, Bot, Eye, PackageOpen } from 'lucide-react';
import { AutomationJobImageGallery } from './AutomationJobImageGallery';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import JSZip from 'jszip';
import { useAvailableModels } from '@/hooks/useAvailableModels';

const IMAGE_SIZES = [
  { value: '1024x1024', label: '1024x1024 (Square)' },
  { value: '1792x1024', label: '1792x1024 (Landscape)' },
  { value: '1024x1792', label: '1024x1792 (Portrait)' }
];

export const AdminAutomationAgent = () => {
  const { jobs, loading, processing, createJob, startJob, pauseJob, stopJob, deleteJob } = useAutomationJobs();
  const { models: availableModels, loading: modelsLoading } = useAvailableModels();
  const [jobName, setJobName] = useState('');
  const [basePrompt, setBasePrompt] = useState('');
  const [totalImages, setTotalImages] = useState('100');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [size, setSize] = useState('1024x1024');
  const [quality, setQuality] = useState('standard');
  const [style, setStyle] = useState('vivid');
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [creatingJob, setCreatingJob] = useState(false);
  const [viewJobId, setViewJobId] = useState<string | null>(null);
  const [viewJobName, setViewJobName] = useState<string>('');


  const handleCreateJob = async () => {
    if (!jobName.trim()) {
      toast.error('Please enter a job name');
      return;
    }
    if (!basePrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    if (selectedModels.length === 0) {
      toast.error('Please select at least one model');
      return;
    }

    const target = parseInt(totalImages);
    if (isNaN(target) || target < 1) {
      toast.error('Please enter a valid number of images');
      return;
    }

    setCreatingJob(true);
    const job = await createJob({
      job_name: jobName,
      base_prompt: basePrompt,
      models_to_use: selectedModels,
      total_images_target: target,
      size,
      quality,
      style,
      rpm_limit: 5
    });

    if (job) {
      setJobName('');
      setBasePrompt('');
      setTotalImages('100');
      setSelectedModels([]);
    }
    setCreatingJob(false);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'pending': 'secondary',
      'running': 'default',
      'paused': 'outline',
      'completed': 'default',
      'failed': 'destructive',
      'stopped': 'outline'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const downloadJobImages = async (job: AutomationJob) => {
    try {
      toast.loading('Preparing download...');
      
      const { data: images, error } = await supabase
        .from('generated_images')
        .select('storage_path, image_url')
        .eq('automated_job_id', job.id);

      if (error) throw error;

      if (!images || images.length === 0) {
        toast.error('No images found for this job');
        return;
      }

      toast.success(`Found ${images.length} images. Downloading...`);

      // Download images one by one
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const link = document.createElement('a');
        link.href = img.image_url;
        link.download = `${job.job_name}_${i + 1}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      toast.success(`Downloaded ${images.length} images`);
    } catch (error) {
      console.error('Error downloading images:', error);
      toast.error('Failed to download images');
    }
  };

  const downloadAllImages = async () => {
    const id = toast.loading('Preparing to download all images...');
    try {
      const { data: images, error } = await supabase
        .from('generated_images')
        .select('image_url, automated_job_id')
        .not('automated_job_id', 'is', null);

      if (error) throw error;

      if (!images || images.length === 0) {
        toast.error('No images found', { id });
        return;
      }

      toast.loading(`Downloading ${images.length} images...`, { id });

      const zip = new JSZip();

      // Download and add images to zip
      for (let i = 0; i < images.length; i++) {
        try {
          const response = await fetch(images[i].image_url, { cache: 'no-store' });
          const blob = await response.blob();
          zip.file(`automation_image_${i + 1}.png`, blob);

          if ((i + 1) % 10 === 0) {
            toast.loading(`Processing ${i + 1}/${images.length} images...`, { id });
          }
        } catch (err) {
          console.error(`Failed to download image ${i + 1}:`, err);
        }
      }

      toast.loading('Generating zip file...', { id });

      // Generate zip file without compression to preserve image quality
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'STORE' // No compression to preserve image quality
      });

      // Download zip file
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(zipBlob);
      link.href = objectUrl;
      link.download = `automation_images_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);

      toast.success(`Downloaded ${images.length} images as zip file`, { id });
    } catch (error) {
      console.error('Error downloading all images:', error);
      toast.error('Failed to download images', { id });
    }
  };

  const getProgressPercentage = (job: AutomationJob) => {
    return (job.images_generated / job.total_images_target) * 100;
  };

  const getETA = (job: AutomationJob) => {
    if (job.status !== 'running' || job.images_generated === 0) return 'N/A';
    
    const imagesRemaining = job.total_images_target - job.images_generated;
    const minutesRemaining = Math.ceil(imagesRemaining / job.rpm_limit);
    
    if (minutesRemaining < 60) {
      return `${minutesRemaining}m`;
    } else {
      const hours = Math.floor(minutesRemaining / 60);
      const minutes = minutesRemaining % 60;
      return `${hours}h ${minutes}m`;
    }
  };


  if (loading || modelsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {jobs.filter(j => j.status === 'running').length}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Generated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {jobs.reduce((sum, j) => sum + j.images_generated, 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(() => {
                const totalSuccess = jobs.reduce((sum, j) => sum + j.success_count, 0);
                const totalRequests = jobs.reduce((sum, j) => sum + j.success_count + j.error_count, 0);
                return totalRequests > 0 ? `${Math.round((totalSuccess / totalRequests) * 100)}%` : '0%';
              })()}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-background/40 backdrop-blur-md border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">RPM Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {jobs.filter(j => j.status === 'running').length * 5}/5
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create New Job */}
      <Card className="bg-background/40 backdrop-blur-md border-border/50">
        <CardHeader>
          <CardTitle>Create Automation Job</CardTitle>
          <CardDescription>
            Set up a new image generation job that runs automatically
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="job-name">Job Name</Label>
              <Input
                id="job-name"
                placeholder="E.g., Product Mockups"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="total-images">Total Images</Label>
              <Input
                id="total-images"
                type="number"
                min="1"
                placeholder="100"
                value={totalImages}
                onChange={(e) => setTotalImages(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="base-prompt">Base Prompt</Label>
            <Textarea
              id="base-prompt"
              placeholder="A professional product photo with white background..."
              value={basePrompt}
              onChange={(e) => setBasePrompt(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Image Size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_SIZES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quality</Label>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="hd">HD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vivid">Vivid</SelectItem>
                  <SelectItem value="natural">Natural</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Models (select multiple)</Label>
            <div className="grid gap-2 md:grid-cols-2">
              {availableModels.map((model) => (
                <label
                  key={model.id}
                  className="flex items-center space-x-2 cursor-pointer rounded-md border p-2 hover:bg-accent"
                >
                  <input
                    type="checkbox"
                    checked={selectedModels.includes(model.model_key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedModels([...selectedModels, model.model_key]);
                      } else {
                        setSelectedModels(selectedModels.filter(m => m !== model.model_key));
                      }
                    }}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{model.display_name}</span>
                </label>
              ))}
            </div>
          </div>

          <Button onClick={handleCreateJob} disabled={creatingJob} className="w-full">
            {creatingJob ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Job'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Active Jobs */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Jobs</h3>
          {jobs.some(job => job.images_generated > 0) && (
            <Button 
              onClick={downloadAllImages}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <PackageOpen className="h-4 w-4 mr-2" />
              Download All Images
            </Button>
          )}
        </div>
        {jobs.length === 0 ? (
          <Card className="bg-background/40 backdrop-blur-md border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-8 text-center">
              <Bot className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No automation jobs yet. Create one above!</p>
            </CardContent>
          </Card>
        ) : (
          jobs.map((job) => (
            <Card key={job.id} className="bg-background/40 backdrop-blur-md border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{job.job_name}</CardTitle>
                    <CardDescription className="mt-1 line-clamp-1">
                      {job.base_prompt}
                    </CardDescription>
                  </div>
                  {getStatusBadge(job.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progress: {job.images_generated} / {job.total_images_target}</span>
                    <span>ETA: {getETA(job)}</span>
                  </div>
                  <Progress value={getProgressPercentage(job)} className="h-2" />
                </div>

                <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                  <span>Models: {job.models_to_use.length}</span>
                  <span>•</span>
                  <span>Size: {job.size}</span>
                  <span>•</span>
                  <span>Quality: {job.quality}</span>
                  <span>•</span>
                  <span>RPM: {job.rpm_limit}</span>
                </div>

                {job.last_error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                    Error: {job.last_error}
                  </div>
                )}

                <div className="flex gap-2">
                  {job.status === 'pending' && (
                    <Button size="sm" onClick={() => startJob(job.id)}>
                      <Play className="h-4 w-4 mr-1" />
                      Start
                    </Button>
                  )}
                  {job.status === 'running' && (
                    <Button size="sm" variant="outline" onClick={() => pauseJob(job.id)}>
                      <Pause className="h-4 w-4 mr-1" />
                      Pause
                    </Button>
                  )}
                  {job.status === 'paused' && (
                    <Button size="sm" onClick={() => startJob(job.id)}>
                      <Play className="h-4 w-4 mr-1" />
                      Resume
                    </Button>
                  )}
                  {(job.status === 'running' || job.status === 'paused') && (
                    <Button size="sm" variant="destructive" onClick={() => stopJob(job.id)}>
                      <Square className="h-4 w-4 mr-1" />
                      Stop
                    </Button>
                  )}
                  {job.images_generated > 0 && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setViewJobId(job.id);
                          setViewJobName(job.job_name);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View ({job.images_generated})
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadJobImages(job)}>
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteJobId(job.id)}
                    className="ml-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteJobId} onOpenChange={() => setDeleteJobId(null)}>
        <AlertDialogContent className="bg-background/40 backdrop-blur-md border-border/50">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this automation job and all its associated images. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteJobId) {
                  deleteJob(deleteJobId);
                  setDeleteJobId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Gallery Dialog */}
      {viewJobId && (
        <AutomationJobImageGallery
          jobId={viewJobId}
          jobName={viewJobName}
          open={!!viewJobId}
          onOpenChange={(open) => {
            if (!open) {
              setViewJobId(null);
              setViewJobName('');
            }
          }}
        />
      )}
    </div>
  );
};
